import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { apiService } from '../../services/api';
import { Search, Camera } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  if (url.startsWith('/uploads/')) {
    return url;
  }
  const normalizedPath = url.startsWith('/') ? url : `/${url}`;
  const baseUrl = (BACKEND_URL || '').replace(/\/api\/?$/, '');
  if (!baseUrl) return normalizedPath;
  return `${baseUrl}${normalizedPath}`;
};

const POSITIONS = [
  { value: 'GK', label: 'Kiper (GK)' },
  { value: 'DEF', label: 'Belakang (DEF)' },
  { value: 'MID', label: 'Tengah (MID)' },
  { value: 'FWD', label: 'Depan (FWD)' },
];

const ITEMS_PER_PAGE = 20;

const resolveParticipantType = (participant = {}) => {
  const explicitType = (participant.participant_type || '').toLowerCase().trim();
  if (explicitType === 'club' || explicitType === 'klub') return 'club';
  if (explicitType === 'school' || explicitType === 'sekolah') return 'school';

  const name = (participant.name || '').toLowerCase().trim();
  if (
    name.startsWith('sd ') ||
    name.startsWith('sdn ') ||
    name.startsWith('sdit ') ||
    name.startsWith('mi ') ||
    name.startsWith('min ') ||
    name.startsWith('mts ') ||
    name.startsWith('smp ') ||
    name.startsWith('sma ') ||
    name.startsWith('smk ') ||
    name.startsWith('tk ')
  ) {
    return 'school';
  }

  return 'club';
};

const AdminPlayers = () => {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all'); // 'all' | 'school' | 'club'
  const [teamFilter, setTeamFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [positionFilter, setPositionFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const { toast } = useToast();

  useEffect(() => {
    loadPlayers();
  }, []);

  const loadPlayers = async () => {
    setLoading(true);
    try {
      const response = await apiService.admin.getParticipants();
      const allPlayers = [];
      
      // Collect all players from all participants
      for (const participant of response.data) {
        if (participant.players && Array.isArray(participant.players)) {
          for (const player of participant.players) {
            const participantType = resolveParticipantType(participant);
            allPlayers.push({
              ...player,
              team_name: participant.name,
              team_id: participant.id,
              participant_type: participantType,
              category: participant.category || '',
              city: participant.city || '',
            });
          }
        }
      }
      
      setPlayers(allPlayers);
      setCurrentPage(1);
    } catch (error) {
      toast({ title: 'Error', description: 'Gagal memuat data pemain', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  // Filter players by type and search
  const filteredPlayers = players
    .filter((p) => typeFilter === 'all' || p.participant_type === typeFilter)
    .filter((p) => teamFilter === 'all' || p.team_name === teamFilter)
    .filter((p) => categoryFilter === 'all' || p.category === categoryFilter)
    .filter((p) => positionFilter === 'all' || p.position === positionFilter)
    .filter((p) =>
      !search.trim() ||
      (p.name || '').toLowerCase().includes(search.toLowerCase()) ||
      (p.team_name || '').toLowerCase().includes(search.toLowerCase()) ||
      (p.city || '').toLowerCase().includes(search.toLowerCase())
    );

  const teamOptions = Array.from(new Set(players.map((p) => p.team_name).filter(Boolean))).sort();
  const categoryOptions = Array.from(new Set(players.map((p) => p.category).filter(Boolean))).sort();

  const totalPages = Math.ceil(filteredPlayers.length / ITEMS_PER_PAGE);
  const startIdx = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIdx = startIdx + ITEMS_PER_PAGE;
  const paginatedPlayers = filteredPlayers.slice(startIdx, endIdx);

  const getPositionLabel = (position) => {
    const pos = POSITIONS.find((p) => p.value === position);
    return pos ? pos.label : position;
  };

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            Daftar Pemain
            <span className="ml-3 text-lg font-normal text-gray-500">({filteredPlayers.length})</span>
          </h1>
          <p className="text-gray-500 mt-1 text-sm">Total pemain terdaftar: {players.length}</p>
        </div>

        {/* Type Filter + Search */}
        <div className="bg-white rounded-xl shadow p-4 mb-6 space-y-3">
          <div className="flex gap-2">
            {[
              { value: 'all', label: 'Semua' },
              { value: 'school', label: 'Sekolah' },
              { value: 'club', label: 'Klub' },
            ].map((opt) => (
              <button
                key={opt.value}
                onClick={() => { setTypeFilter(opt.value); setCurrentPage(1); }}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${
                  typeFilter === opt.value
                    ? 'bg-green-600 text-white'
                    : 'border border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <select
              value={teamFilter}
              onChange={(e) => { setTeamFilter(e.target.value); setCurrentPage(1); }}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="all">Semua Tim</option>
              {teamOptions.map((team) => (
                <option key={team} value={team}>{team}</option>
              ))}
            </select>
            <select
              value={categoryFilter}
              onChange={(e) => { setCategoryFilter(e.target.value); setCurrentPage(1); }}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="all">Semua Kategori</option>
              {categoryOptions.map((category) => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
            <select
              value={positionFilter}
              onChange={(e) => { setPositionFilter(e.target.value); setCurrentPage(1); }}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="all">Semua Posisi</option>
              {POSITIONS.map((pos) => (
                <option key={pos.value} value={pos.value}>{pos.label}</option>
              ))}
            </select>
          </div>
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Cari nama pemain atau tim..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            <button
              type="submit"
              className="px-5 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition"
            >
              Cari
            </button>
          </form>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-center py-16 text-gray-500">Memuat data...</div>
        ) : filteredPlayers.length === 0 ? (
          <div className="bg-white rounded-xl shadow p-10 text-center text-gray-500">
            Tidak ada pemain ditemukan.
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-white rounded-xl shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left px-4 py-3 text-gray-600 font-semibold w-10">No</th>
                      <th className="text-left px-4 py-3 text-gray-600 font-semibold w-16">Foto</th>
                      <th className="text-left px-4 py-3 text-gray-600 font-semibold">Nama Pemain</th>
                      <th className="text-left px-4 py-3 text-gray-600 font-semibold">No. Punggung</th>
                      <th className="text-left px-4 py-3 text-gray-600 font-semibold">Posisi</th>
                      <th className="text-left px-4 py-3 text-gray-600 font-semibold">Tim</th>
                      <th className="text-left px-4 py-3 text-gray-600 font-semibold">Kategori</th>
                      <th className="text-left px-4 py-3 text-gray-600 font-semibold">Kota</th>
                      <th className="text-left px-4 py-3 text-gray-600 font-semibold">Jenis Peserta</th>
                      <th className="text-left px-4 py-3 text-gray-600 font-semibold">Tgl. Lahir</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {paginatedPlayers.map((player, idx) => (
                      <tr key={`${player.team_id}-${player.number}-${idx}`} className="hover:bg-gray-50 transition">
                        <td className="px-4 py-3 text-gray-400">{startIdx + idx + 1}</td>
                        <td className="px-4 py-3">
                          {player.photo ? (
                            <img
                              src={getImageUrl(player.photo)}
                              alt={player.name}
                              className="h-12 w-12 object-cover rounded-lg"
                            />
                          ) : (
                            <div className="h-12 w-12 rounded-lg bg-gray-200 flex items-center justify-center">
                              <Camera className="h-6 w-6 text-gray-400" />
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 font-medium text-gray-900">{player.name || '-'}</td>
                        <td className="px-4 py-3 text-gray-600">#{player.number || '-'}</td>
                        <td className="px-4 py-3 text-gray-600">{getPositionLabel(player.position)}</td>
                        <td className="px-4 py-3 text-gray-600">{player.team_name}</td>
                        <td className="px-4 py-3 text-gray-600">{player.category || '-'}</td>
                        <td className="px-4 py-3 text-gray-600">{player.city || '-'}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${player.participant_type === 'club' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>
                            {player.participant_type === 'club' ? 'Klub' : 'Sekolah'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{player.birth_date || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-2 px-4 py-3 bg-white rounded-lg shadow">
                <div className="text-sm text-gray-600">
                  Halaman {currentPage} dari {totalPages} | Total {filteredPlayers.length} pemain
                </div>
                <div className="flex flex-wrap gap-1 justify-center">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    ←
                  </button>
                  {(() => {
                    const pages = [];
                    const delta = 2;
                    const left = Math.max(1, currentPage - delta);
                    const right = Math.min(totalPages, currentPage + delta);
                    if (left > 1) {
                      pages.push(1);
                      if (left > 2) pages.push('...');
                    }
                    for (let p = left; p <= right; p++) pages.push(p);
                    if (right < totalPages) {
                      if (right < totalPages - 1) pages.push('...');
                      pages.push(totalPages);
                    }
                    return pages.map((page, idx) =>
                      page === '...' ? (
                        <span key={`ellipsis-${idx}`} className="px-2 py-1 text-sm text-gray-400">…</span>
                      ) : (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                            currentPage === page ? 'bg-green-600 text-white' : 'border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      )
                    );
                  })()}
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    →
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminPlayers;
